<template>
  <div id="app">
    <div v-if="isLoading" class="loading-overlay">
      <div class="loading-spinner"></div>
      <p>Chargement...</p>
    </div>
    <router-view v-else />
    <NotificationList />
    
    <!-- Buddi Assistant Modal (implémentation directe) -->
    <div v-if="buddiVisible" class="buddi-modal-container">
      <div class="buddi-modal-overlay" @click="closeBuddi"></div>
      <div class="buddi-modal">
        <!-- En-tête avec logo et titre -->
        <div class="chat-header">
          <div class="bot-avatar">
            <img src="./assets/buddi-avatar.png" alt="BuddiBot">
          </div>
          <h1>{{ buddiTitle }}</h1>
          <button class="close-button" @click="closeBuddi">×</button>
        </div>

        <!-- Zone de chat -->
        <div class="chat-messages" ref="chatMessages">
          <!-- Tous les messages (bot et utilisateur) dans l'ordre chronologique -->
          <div v-for="message in allMessages" :key="message.id"
               :class="['message', message.sender === 'bot' ? 'bot-message' : 'user-message']">
            <div v-if="message.sender === 'bot'" class="bot-avatar-small">
              <img src="./assets/buddi-avatar.png" alt="BuddiBot">
            </div>
            <div v-else class="user-avatar-small">
              <img :src="userAvatarUrl || 'https://cdn.discordapp.com/embed/avatars/0.png'" alt="Utilisateur" @error="$event.target.src='https://cdn.discordapp.com/embed/avatars/0.png'">
            </div>
            <div class="message-content" v-html="message.content"></div>
          </div>
          
          <!-- Indicateur de saisie -->
          <div v-if="isTyping" class="message bot-message typing-indicator">
            <div class="bot-avatar-small">
              <img src="./assets/buddi-avatar.png" alt="BuddiBot">
            </div>
            <div class="typing-dots">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        </div>

        <!-- Zone d'interaction -->
        <div class="chat-interaction">
          <div v-if="buddiOptions.length > 0" class="options-container">
            <button
              v-for="(option, index) in buddiOptions"
              :key="index"
              @click="handleOptionClick(option)"
              class="option-button"
              :class="{ 'primary': option.primary }"
            >
              {{ option.text }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import NotificationList from './components/NotificationList.vue'
import { ref, onMounted, provide, reactive } from 'vue'
import { useSubscriptionData } from './composables/useSubscriptionData'
import { authService } from './services/auth'

export default {
  name: 'App',
  components: {
    NotificationList
  },
  setup() {
    const isLoading = ref(true)
    const { fetchSubscriptionData } = useSubscriptionData()
    
    // État pour la modale Buddi
    const buddiVisible = ref(false)
    const buddiTitle = ref('Assistance BuddiBot')
    const buddiOptions = ref([])
    const isTyping = ref(false)
    const allMessages = ref([])
    const userAvatarUrl = ref('')
    let messageIdCounter = 0
    
    // Créer un état global pour le service Buddi
    const buddiState = reactive({
      visible: false,
      title: 'Assistance BuddiBot',
      messages: [],
      options: [],
      closeOnClickOutside: true
    })
    
    // Fournir l'état global pour le service Buddi
    provide('buddiState', buddiState)
    
    // Créer le service Buddi
    const buddiService = {
      show(options = {}) {
        console.log('BuddiService.show() appelé', options)
        buddiTitle.value = options.title || 'Assistance BuddiBot'
        
        // Réinitialiser les messages et les options
        allMessages.value = []
        buddiOptions.value = []
        
        // Ajouter les messages initiaux avec un délai
        if (options.messages && options.messages.length > 0) {
          let delay = 0
          options.messages.forEach((message) => {
            setTimeout(() => {
              // Afficher l'indicateur de typing avant chaque message
              isTyping.value = true
              
              setTimeout(() => {
                isTyping.value = false
                addMessage(message, 'bot')
              }, 1000) // Durée de l'animation de typing
            }, delay)
            
            delay += 1500 // 1.5s entre chaque message initial
          })
          
          // Ajouter les options après les messages
          if (options.options && options.options.length > 0) {
            setTimeout(() => {
              buddiOptions.value = options.options || []
            }, delay + 500)
          }
        } else {
          // Si pas de messages, ajouter directement les options
          buddiOptions.value = options.options || []
        }
        
        buddiVisible.value = true
        return this
      },
      hide() {
        buddiVisible.value = false
        return this
      },
      addBotMessage(message) {
        // Afficher l'indicateur de typing
        isTyping.value = true
        
        setTimeout(() => {
          isTyping.value = false
          addMessage(message, 'bot')
        }, 1000)
        
        return this
      },
      setOptions(options) {
        buddiOptions.value = options
        return this
      }
    }
    
    // Fournir le service Buddi
    provide('buddiService', buddiService)
    
    // Méthodes pour la modale Buddi
    const closeBuddi = () => {
      buddiVisible.value = false
    }
    
    // Fonction pour ajouter un message à la conversation
    const addMessage = (content, sender) => {
      allMessages.value.push({
        id: `msg-${messageIdCounter++}`,
        content,
        sender,
        timestamp: Date.now()
      })
      
      // Faire défiler vers le bas
      setTimeout(() => {
        const chatMessages = document.querySelector('.chat-messages')
        if (chatMessages) {
          chatMessages.scrollTop = chatMessages.scrollHeight
        }
      }, 50)
    }
    
    const handleOptionClick = (option) => {
      // Vider les options actuelles
      buddiOptions.value = []
      
      // Ajouter le message utilisateur
      if (option.text) {
        addMessage(option.text, 'user')
      }
      
      // Exécuter le callback si présent
      if (option.callback) {
        option.callback()
      }
      
      // Ajouter les réponses avec un délai
      if (option.responses && option.responses.length > 0) {
        // Afficher l'indicateur de saisie
        isTyping.value = true
        
        // Délai avant d'afficher la première réponse
        setTimeout(() => {
          // Masquer l'indicateur de saisie après le délai
          isTyping.value = false
          
          // Ajouter les réponses une par une avec un délai entre chacune
          let totalDelay = 0
          
          option.responses.forEach((response, index) => {
            totalDelay = index * 800 // 800ms entre chaque message
            
            setTimeout(() => {
              // Afficher l'indicateur de saisie avant chaque message
              if (index > 0) isTyping.value = true
              
              setTimeout(() => {
                // Masquer l'indicateur de saisie et afficher le message
                isTyping.value = false
                addMessage(response, 'bot')
              }, 1000) // Durée de l'animation de typing
            }, totalDelay)
          })
          
          // Définir les nouvelles options après les réponses
          if (option.nextOptions) {
            setTimeout(() => {
              buddiOptions.value = option.nextOptions
            }, totalDelay + 1800) // Attendre que toutes les réponses soient affichées + délai
          }
        }, 800) // 800ms de délai initial après la question
      } else if (option.nextOptions) {
        // Si pas de réponses mais des options suivantes
        setTimeout(() => {
          buddiOptions.value = option.nextOptions
        }, 500)
      }
    }

    // Rendre le service Buddi disponible globalement immédiatement
    window.$buddiAssistant = buddiService
    
    onMounted(async () => {
      try {
        // Charger les données utilisateur Discord en premier
        await authService.getUserInfo()
        // Puis charger les données d'abonnement
        await fetchSubscriptionData()
        
        // Récupérer l'avatar de l'utilisateur
        const userData = authService.getUserData()
        if (userData && userData.discordAvatarUrl) {
          userAvatarUrl.value = userData.discordAvatarUrl
        }
      } catch (err) {
        console.error('Error loading initial data:', err)
      } finally {
        isLoading.value = false
      }
    })

    return {
      isLoading,
      buddiVisible,
      buddiTitle,
      buddiOptions,
      allMessages,
      isTyping,
      closeBuddi,
      handleOptionClick
    }
  }
}
</script>

<style>
#app {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: white;
  background: transparent; /* use global gradient */
  min-height: 100vh;
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, var(--dark-color), var(--darker-color));
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 3px solid rgba(255,255,255,0.25);
  border-top: 3px solid var(--dark-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

.loading-overlay p {
  color: #A0AEC0;
}

/* Buddi modal styles are shared in components.css (section 15). */
.message {
  animation: message-appear 0.3s ease-out forwards;
}

/* Animation de typing */
.typing-indicator {
  opacity: 0.7;
}

.typing-dots {
  display: flex;
  padding: 12px 16px;
  background: rgba(1, 110, 126, 0.3);
  border-radius: 16px;
  border-top-left-radius: 4px;
  gap: 4px;
}

.typing-dots span {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: white;
  display: inline-block;
  animation: typing-dot 1.4s infinite ease-in-out both;
}

.typing-dots span:nth-child(1) {
  animation-delay: -0.32s;
}

.typing-dots span:nth-child(2) {
  animation-delay: -0.16s;
}

@keyframes typing-dot {
  0%, 80%, 100% {
    transform: scale(0.6);
    opacity: 0.6;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes message-appear {
  0% {
    opacity: 0;
    transform: translateY(10px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
